/**
 * The two calls the unauthenticated sign-in card makes.
 *
 * Both are `anonymous: true`: they run before a session exists, so sending an `Authorization`
 * header would be sending a token nobody has. Neither returns anything belonging to a provider —
 * the availability list is a set of booleans, and the email hand-back answers with the address
 * the person just typed.
 */

import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'

import { apiV1 } from '../../../lib/api'
import { useBranding } from '../../../lib/branding'
import type { OAuthEmailResult, OAuthEmailWire, SocialProvider, SocialProviderWire } from './contract'
import { readAvailableProviders, readOAuthEmailResult, socialSignInEnabled } from './contract'

/** Its own key: `lib/query.ts` is the shared factory and belongs to another agent. */
export const SOCIAL_PROVIDERS_KEY = ['pd', 'auth', 'oauth-providers'] as const

/**
 * `GET /api/v1/auth/oauth/providers` — which of Google and Facebook this deployment registered.
 *
 * Registration is an operator fact, not a tenant one, so it is cached for the session rather
 * than refetched per card.
 */
export const fetchSocialProviders = async (): Promise<SocialProvider[]> => {
  const wire = await apiV1<SocialProviderWire[]>('/auth/oauth/providers', { anonymous: true })
  return readAvailableProviders(Array.isArray(wire) ? wire : [])
}

export const useSocialProvidersQuery = (): UseQueryResult<SocialProvider[]> =>
  useQuery({
    queryKey: SOCIAL_PROVIDERS_KEY,
    queryFn: fetchSocialProviders,
    staleTime: 10 * 60_000,
  })

export interface SocialSignInOptions {
  /** Providers to render, already filtered by registration and by the tenant switch. */
  providers: SocialProvider[]
  status: 'pending' | 'error' | 'success'
  error: unknown
  retry: () => void
  /**
   * True when this tenant switched social sign-in off. The card renders nothing at all in that
   * case — not a disabled button, not an explanation nobody asked for.
   */
  tenantDisabled: boolean
}

/**
 * What the sign-in and sign-up cards ask.
 *
 * Two independent reasons to show nothing, kept separate because they are different facts:
 * the operator registered no app (`providers` is empty), or the white-label tenant turned the
 * whole thing off (`tenantDisabled`). Only the first is worth a retry.
 */
export const useSocialSignInOptions = (): SocialSignInOptions => {
  const { branding } = useBranding()
  const enabled = socialSignInEnabled(branding)
  const query = useSocialProvidersQuery()

  return {
    providers: enabled ? (query.data ?? []) : [],
    status: query.status,
    error: query.error,
    retry: () => {
      void query.refetch()
    },
    tenantDisabled: !enabled,
  }
}

/**
 * `POST /api/v1/auth/oauth/:provider/email` — an address for an identity that arrived without one.
 *
 * `state` is the same single-use row the callback parked the identity on. Nothing is created
 * until the address is verified, which is why the answer is "sent", not "signed in".
 */
export const submitOAuthEmail = async (
  provider: SocialProvider,
  state: string,
  email: string,
): Promise<OAuthEmailResult> => {
  const wire = await apiV1<OAuthEmailWire>(`/auth/oauth/${provider}/email`, {
    method: 'POST',
    json: { state, email },
    anonymous: true,
  })
  return readOAuthEmailResult(wire ?? {}, email)
}
